/**
 * Paused scans: how long one waits before Archive and Delete permanently are offered on it
 * (DEC-192 decision 1). A workspace setting, a quarter (90 days) by default, 1 to 3650.
 * Settings is admin-only, and the server checks the range and the profile again.
 * No em-dashes (DEC-069).
 */
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Archive } from 'lucide-react'
import { settingsApi } from '../../api/settings'
import { SCANS_KEY } from '../../hooks/useScanQueries'
import { Button } from '../shared/Button'

export const ARCHIVE_DAYS_DEFAULT = 90
export const ARCHIVE_DAYS_MIN = 1
export const ARCHIVE_DAYS_MAX = 3650

export function ScanArchiveCard({ days }: { days: number | undefined }) {
  const queryClient = useQueryClient()
  const current = days ?? ARCHIVE_DAYS_DEFAULT
  const [draft, setDraft] = useState(String(current))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => setDraft(String(current)), [current])

  const n = Number(draft)
  const valid = Number.isInteger(n) && n >= ARCHIVE_DAYS_MIN && n <= ARCHIVE_DAYS_MAX

  const save = useMutation({
    mutationFn: () => settingsApi.setArchivePausedAfterDays(n),
    onSuccess: () => {
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['settings'] })
      // Every paused row's "offered on <date>" moves with this number.
      void queryClient.invalidateQueries({ queryKey: SCANS_KEY })
    },
    onError: (e: Error) => setError(e.message),
  })

  return (
    <div className="bg-panel border border-hairline rounded-md overflow-hidden mb-4" data-testid="scan-archive-card">
      <div className="px-4 py-3 border-b border-fill-subtle font-semibold text-[10px] tracking-[2px] uppercase text-muted">
        Paused scans
      </div>
      <div className="px-4 py-[14px]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-ground border border-hairline flex items-center justify-center flex-none">
            <Archive className="w-4 h-4 text-accent-text" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[13px] leading-tight">
              Archive and Delete permanently after {current} {current === 1 ? 'day' : 'days'} paused
            </div>
            <div className="text-[11px] leading-tight text-muted mt-[2px]">
              Until then a paused scan offers only Resume and Stop. Finished and failed scans can be archived at any time.
            </div>
          </div>
        </div>
        <label htmlFor="archive-paused-after-days" className="block font-medium text-[12px] mb-1">
          Days a scan stays paused first
        </label>
        <div className="flex items-center gap-2">
          <input
            id="archive-paused-after-days"
            type="number"
            inputMode="numeric"
            min={ARCHIVE_DAYS_MIN}
            max={ARCHIVE_DAYS_MAX}
            step={1}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-[120px] border border-control rounded-md px-3 py-2 text-[13px] tabular-nums focus:outline-none focus-visible:shadow-focus"
          />
          <Button size="sm" loading={save.isPending} disabled={!valid || n === current} onClick={() => save.mutate()}>
            Save
          </Button>
          {save.isSuccess && !error && n === current && <span className="text-[11px] text-muted">Saved.</span>}
        </div>
        {!valid && (
          <p className="text-[12px] text-critical mt-2">
            Enter a whole number of days from {ARCHIVE_DAYS_MIN} to {ARCHIVE_DAYS_MAX}.
          </p>
        )}
        <p className="text-[10px] leading-[1.55] text-muted mt-[5px]">
          A quarter (90 days) unless you change it. Measured from when the scan started, since TDIA does not record the moment it was paused.
        </p>
        {error && <p className="text-[12px] text-critical mt-2">{error}</p>}
      </div>
    </div>
  )
}
